/**
 * 员工权限与企业微信关联的错误识别 (纯函数, 不依赖 Vue 与浏览器; 单测在 apps/workbench/test/internal-errors.test.ts).
 * 服务器错误体是工作台约定的 camelCase, 也认 PascalCase.
 */

/** 权限代码对应的名称 (与工作台 设置 · 人员与权限 一致) */
const PERMISSION_NAME: Record<string, string> = {
  staff: '人员与权限',
  integrations: '接入设置',
  refunds: '退款审核',
  backend: '运营后台',
};

function parseObject(bodyText: string): Record<string, unknown> | null {
  if (!bodyText.trim().startsWith('{')) return null;
  try {
    const j = JSON.parse(bodyText) as unknown;
    return j && typeof j === 'object' && !Array.isArray(j) ? (j as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

const field = (j: Record<string, unknown>, name: string): unknown => j[name] ?? j[name.charAt(0).toUpperCase() + name.slice(1)];

/** 403 permissionDenied 给员工看的话: 缺哪项权限、找谁勾选 */
export function permissionDeniedText(permission?: string | null): string {
  const name = PERMISSION_NAME[permission || 'backend'] ?? permission ?? '运营后台';
  return `没有「${name}」权限，请负责人在工作台「设置 · 人员与权限」中开启`;
}

/** 403 正文是 { code: 'permissionDenied', permission } 时返回缺的权限 (可能为空串), 否则 null */
export function permissionDeniedOf(status: number, bodyText: string): string | null {
  if (status !== 403) return null;
  const j = parseObject(bodyText);
  if (!j || field(j, 'code') !== 'permissionDenied') return null;
  const permission = field(j, 'permission');
  return typeof permission === 'string' ? permission : '';
}

/** 403 正文是 { code: 'wxworkNotLinked', wxworkUserId, name } 时返回成员 ID 与名字 (名字可能为空串), 否则 null */
export function wxworkNotLinkedBody(status: number, bodyText: string): { wxworkUserId: string; name: string } | null {
  if (status !== 403) return null;
  const j = parseObject(bodyText);
  if (!j || field(j, 'code') !== 'wxworkNotLinked') return null;
  const id = field(j, 'wxworkUserId');
  const name = field(j, 'name');
  return { wxworkUserId: typeof id === 'string' ? id : '', name: typeof name === 'string' ? name.trim() : '' };
}

/** 企微成员尚未关联工作台账号时显示给该成员的话; 没有名字时不带括号 */
export function wxworkNotLinkedText(name?: string | null): string {
  return `你的企微${name ? `（${name}）` : ''}尚未关联工作台账号，请负责人在工作台「设置 · 人员与权限」中关联`;
}

/** 出错正文里服务器给的说明: message / detail / title, 都没有时为 null */
export function serverMessageOf(bodyText: string): string | null {
  const j = parseObject(bodyText);
  if (!j) return null;
  for (const name of ['message', 'detail', 'title', 'error']) {
    const v = field(j, name);
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}
